import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AutoComplete } from "antd";
import {
  Edit,
  ListCheck,
  Plus,
  X,
  Tag,
  FileText,
  Sparkles,
  Gift,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import LoyaltyCardPreview from "../loyalty/LoyaltyCardPreview";
import { fetchServices } from "../../store/loyaltySlices/loyaltyCardsSlice";
import {
  fetchMemberships,
  saveMembership,
  toggleMembershipStatus,
} from "../../store/loyaltySlices/membershipSlice";

import toastr from "toastr";

export default function Membership() {
  useDocumentTitle("Welcome to Admin | Membership");
  const dispatch = useDispatch();
  const { services: loyalty_services } = useSelector((state) => state.loyaltyCards);
  const { memberships, loading, error, totalPages } = useSelector((state) => state.membership);

  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const perPage = 25;

  const initialFormData = {
    program_id: 0,
    program_name: "",
    description: "",
    terms_conditions: "",
    is_active: true,
    services: [""],
    card_tagline: "",
    membership_type: "free",
    membership_amount: "",
    welcome_coin: "",
    referral_coin: "",
    max_referral_account: "",
    referral_earn_type: 1,
    invitation_required: false,
    membership_logo: null, // File object when a new logo is picked, else null
    max_coin_usage_percent: 100,
  };
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  // Existing logo URL (edit mode) or a local object-URL preview of a newly picked file
  const [logoPreview, setLogoPreview] = useState("");
  // True once the user clears a logo (new pick or existing) — tells submit to send remove_logo
  const [logoRemoved, setLogoRemoved] = useState(false);

  const loadMemberships = (page = currentPage) => {
    dispatch(fetchMemberships({ perPage, page, keyword: keyword || null }));
  };

  useEffect(() => {
    dispatch(fetchServices());
    loadMemberships(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, dispatch]);

  const handleOpen = () => {
    setFormData(initialFormData);
    setLogoPreview("");
    setLogoRemoved(false);
    setOpen(true);
  };

  const handleChange = (e, field = "") => {
    let name = "";
    let value = "";
    if (e && e.target) {
      name = e.target.name;
      value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    } else {
      name = field;
      value = e;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setFormData({
      program_id: item.program_id,
      program_name: item.program_name || "",
      description: item.description || "",
      terms_conditions: item.terms_conditions || "",
      is_active: !!item.is_active,
      services: Array.isArray(item.services) && item.services.length
      ? item.services : [""],
      card_tagline: item.card_tagline || "",
      membership_type: item.membership_type || "free",
      membership_amount: item.membership_amount || "",
      welcome_coin: item.welcome_coin ?? "",
      referral_coin: item.referral_coin ?? "",
      referral_earn_type: Number(item.referral_earn_type) || 1,
      max_referral_account: item.max_referral_account ?? "",
      invitation_required: !!item.invitation_required,
      membership_logo: null,
      max_coin_usage_percent: item.max_coin_usage_percent ?? 100,
    });
    setLogoPreview(item.membership_logo || "");
    setLogoRemoved(false);
    setOpen(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, membership_logo: file }));
    setLogoPreview(URL.createObjectURL(file));
    setLogoRemoved(false);
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({ ...prev, membership_logo: null }));
    setLogoPreview("");
    setLogoRemoved(true);
  };

  const handleToggleStatus = async (item) => {
    try {
      const result = await dispatch(toggleMembershipStatus(item.program_id)).unwrap();
      toastr.success(result?.response?.message || "Membership status updated");
    } catch (err) {
      toastr.error(err?.message || "Could not update status");
    }
  };

  // --- Services list (dropdown-with-free-text, "add more" to append rows) ---
  const SERVICE_AC_OPTIONS = (loyalty_services || [])
    .filter((s) => s.status === 1)
    .map((s) => ({ value: s.name }));

  const serviceInputRefs = useRef([]);

  const getServiceOptionsForRow = (currentIndex) => {
    const selectedElsewhere = formData.services.filter(
      (_, i) => i !== currentIndex && Boolean(formData.services[i])
    );
    return SERVICE_AC_OPTIONS.filter((opt) => !selectedElsewhere.includes(opt.value));
  };

  const handleServiceChange = (index, value) => {
    setFormData((prev) => {
      const services = [...prev.services];
      services[index] = value;
      return { ...prev, services };
    });
  };

  const handleAddService = () => {
    setFormData((prev) => ({ ...prev, services: [...prev.services, ""] }));
  };

  const handleRemoveService = (index) => {
    setFormData((prev) => {
      const services = prev.services.filter((_, i) => i !== index);
      return { ...prev, services: services.length ? services : [""] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toastr.options = {
      closeButton: true,
      progressBar: true,
      timeOut: 3000,
      positionClass: "toast-top-center",
    };

    if (formData.program_name.trim() === "") {
      toastr.error("Enter membership name", { timeOut: 2000 });
      return false;
    }
    if (formData.membership_type === "paid" && !formData.membership_amount) {
      toastr.error("Enter membership amount", { timeOut: 2000 });
      return false;
    }
    if (formData.services.filter((s) => s && s.trim() !== "").length === 0) {
      toastr.error("Select at least one service", { timeOut: 2000 });
      return false;
    }

  if (Number(formData.welcome_coin) < 0) {
    toastr.error("Welcome coins cannot be negative", { timeOut: 2000 });
    return false;
  }

  if (formData.referral_coin !== "" && Number(formData.referral_coin) < 0) {
    toastr.error("Referral coins cannot be negative", { timeOut: 2000 });
    return false;
  }

  if (formData.max_referral_account !== "" && Number(formData.max_referral_account) < 1) {
    toastr.error("Max referral accounts must be at least 1", { timeOut: 2000 });
    return false;
  }

  if (formData.max_coin_usage_percent === "" || formData.max_coin_usage_percent === null ||
    !Number.isInteger(Number(formData.max_coin_usage_percent)) || Number(formData.max_coin_usage_percent) < 0 ||
    Number(formData.max_coin_usage_percent) > 100
    ) {
      toastr.error("Max coins usable per transaction must be a whole number between 0 and 100", { timeOut: 2000 });
      return false;
    }

    const payload = new FormData();
    payload.append("program_id", formData.program_id || 0);
    payload.append("program_name", formData.program_name);
    payload.append("description", formData.description || "");
    payload.append("terms_conditions", formData.terms_conditions || "");
    payload.append("is_active", formData.is_active ? 1 : 0);
    formData.services
      .filter((s) => s && s.trim() !== "")
      .forEach((s) => payload.append("services[]", s));
    payload.append("card_tagline", formData.card_tagline || "");
    payload.append("membership_type", formData.membership_type || "free");
    payload.append("welcome_coin", formData.welcome_coin);
    payload.append("referral_coin", formData.referral_coin);
    payload.append("referral_earn_type", formData.referral_earn_type);
    payload.append("max_referral_account", formData.max_referral_account);
    payload.append("max_coin_usage_percent", formData.max_coin_usage_percent);
    if (formData.membership_type === "paid") {
      payload.append("membership_amount", formData.membership_amount);
    }
    payload.append("invitation_required", formData.invitation_required ? 1 : 0);
    // Only attach the file field when a new logo was actually picked —
    // editing without touching the logo should leave the saved one alone.
    if (formData.membership_logo instanceof File) {
      payload.append("membership_logo", formData.membership_logo);
    } else if (logoRemoved) {
      payload.append("remove_logo", 1);
    }

    try {
    const response = await dispatch(saveMembership(payload)).unwrap();
    loadMemberships(currentPage);
    setOpen(false);
    toastr.success(response.message);
    setFormData(initialFormData);
    setLogoPreview("");
    setLogoRemoved(false);
    } catch (err) {
        toastr.error(
            err?.message ||
            err?.error?.program_name?.[0] ||
            "Save failed. Please try again."
        );
    }
  };

  const previewServices = formData.services.filter((s) => s && s.trim() !== "");

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 md:gap-4">
        {error && <div style={{ color: "red" }}>{error}</div>}

        {open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form onSubmit={handleSubmit}>
              <div className="bg-white rounded-lg shadow-xl min-w-[300px] w-[85vw] max-w-[1100px] max-h-[95vh] flex flex-col">
                <div className="flex justify-between items-center border-b px-6 py-4">
                  <h3 className="text-lg font-semibold">
                    {formData.program_id > 0 ? "Edit" : "Add"} Membership
                  </h3>
                  <button type="button" onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-4 flex flex-col lg:flex-row gap-6 overflow-y-auto flex-grow">
                  <div className="flex-1 space-y-4 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* program_name doubles as the membership title on the card preview —
                          membership_title is intentionally not used anywhere in this form */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Membership Name <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <input
                            type="text"
                            name="program_name"
                            value={formData.program_name || ""}
                            onChange={handleChange}
                            placeholder="e.g. Gold Membership"
                            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                          />
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Card Tagline</label>
                        <div className="relative">
                          <input
                            type="text"
                            name="card_tagline"
                            value={formData.card_tagline || ""}
                            onChange={handleChange}
                            placeholder="e.g. Travel more, pay less"
                            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                          />
                          <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Membership Logo</label>
                        <div className="flex items-center gap-3">
                          {logoPreview && (
                            <div className="relative shrink-0">
                              <img
                                src={logoPreview}
                                alt="Logo preview"
                                className="w-10 h-10 rounded-lg object-cover border"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveLogo}
                                className="absolute -top-1.5 -right-1.5 bg-gray-700 text-white rounded-full p-0.5 hover:bg-red-500"
                                aria-label="Remove logo"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          )}
                          <input
                            key={logoPreview ? "has-logo" : "no-logo"}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="w-full text-sm border rounded-xl px-3 py-2 focus:ring-2 focus:ring-[#f4a406]"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Membership Type</label>
                        <div className="relative">
                          <select
                            name="membership_type"
                            value={formData.membership_type || "free"}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                          >
                            <option value="free">Free</option>
                            <option value="paid">Paid</option>
                          </select>
                          <ListCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        </div>
                      </div>

                      {formData.membership_type === "paid" && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Membership Amount <span className="text-red-500">*</span></label>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              name="membership_amount"
                              value={formData.membership_amount || ""}
                              onChange={handleChange}
                              placeholder="0.00"
                              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                            />
                            <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          </div>
                        </div>
                      )}

                      <div>
                    <label className="block text-sm font-medium mb-1">
                      Welcome Coins 
                    </label>

                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        name="welcome_coin"
                        value={formData.welcome_coin ?? ""}
                        onChange={handleChange}
                        placeholder="e.g. 100"
                        className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                      />

                      <Gift
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                    </div>

                    <p className="text-xs text-gray-500 mt-1">
                      Coins awarded to the user as a welcome reward.
                    </p>
                    
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Referral Coins</label>
                    <div className="relative">
                      <input
                        type="number" min="0" step="1"
                        name="referral_coin"
                        value={formData.referral_coin ?? ""}
                        onChange={handleChange}
                        placeholder="e.g. 50"
                        className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                      />
                      <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                     Optional. Coins earned for each successful referral on this membership. Who earns them is set in "Referral Earn" below.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Referral Earn <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <select
                        name="referral_earn_type"
                        value={formData.referral_earn_type || 1}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                      >
                        <option value={1}>Only referrer earns</option>
                        <option value={2}>Referrer &amp; referee both earn</option>
                      </select>
                      <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Who gets the referral coins for each successful referral on this membership.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Referral Accounts</label>
                    <div className="relative">
                      <input
                        type="number" min="1" step="1"
                        name="max_referral_account"
                        value={formData.max_referral_account ?? ""}
                        onChange={handleChange}
                        placeholder="e.g. 10"
                        className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                      />
                      <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional. Maximum accounts one customer can refer and earn coins from.
                    </p>
                  </div>
                  <div>
                  <label className="block text-sm font-medium mb-1">
                    Max Coins Usable Per Transaction (%) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number" min="0" max="100" step="1"
                      name="max_coin_usage_percent"
                      value={formData.max_coin_usage_percent ?? ""}
                      onChange={handleChange}
                      placeholder="e.g. 40"
                      required
                      className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                    />
                    <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Required. Maximum % of a single transaction (membership reward or voucher purchase) that can be paid with coins. 0 = coins can't be used, 100 = fully payable with coins.
                  </p>
                </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Invitation Required</label>
                        <div className="flex items-center gap-2 h-[42px]">
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, invitation_required: false }))}
                            className={`px-4 py-2 rounded-xl border text-sm transition-colors ${
                              !formData.invitation_required
                                ? "bg-[#f4a406] text-white border-[#f4a406]"
                                : "bg-white text-gray-600 border-gray-300"
                            }`}
                          >
                            No
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, invitation_required: true }))}
                            className={`px-4 py-2 rounded-xl border text-sm transition-colors ${
                              formData.invitation_required
                                ? "bg-[#f4a406] text-white border-[#f4a406]"
                                : "bg-white text-gray-600 border-gray-300"
                            }`}
                          >
                            Yes
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Active</label>
                        <div className="flex items-center gap-2 h-[42px]">
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, is_active: true }))}
                            className={`px-4 py-2 rounded-xl border text-sm transition-colors ${
                              formData.is_active
                                ? "bg-[#f4a406] text-white border-[#f4a406]"
                                : "bg-white text-gray-600 border-gray-300"
                            }`}
                          >
                            Active
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, is_active: false }))}
                            className={`px-4 py-2 rounded-xl border text-sm transition-colors ${
                              !formData.is_active
                                ? "bg-[#f4a406] text-white border-[#f4a406]"
                                : "bg-white text-gray-600 border-gray-300"
                            }`}
                          >
                            Inactive
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <div className="relative">
                        <textarea
                          name="description"
                          value={formData.description || ""}
                          onChange={handleChange}
                          rows={2}
                          placeholder="Internal description of the program..."
                          className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                        />
                        <FileText className="absolute left-3 top-3 text-gray-400" size={18} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Terms &amp; Conditions</label>
                      <div className="relative">
                        <textarea
                          name="terms_conditions"
                          value={formData.terms_conditions || ""}
                          onChange={handleChange}
                          rows={3}
                          placeholder="Terms and conditions..."
                          className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
                        />
                        <FileText className="absolute left-3 top-3 text-gray-400" size={18} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Services <span className="text-red-500">*</span></label>
                      <div className="space-y-2">
                        {formData.services.map((service, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <AutoComplete
                              ref={(el) => (serviceInputRefs.current[index] = el)}
                              value={service}
                              options={getServiceOptionsForRow(index)}
                              onChange={(value) => handleServiceChange(index, value)}
                              onSelect={(value) => {
                                handleServiceChange(index, value);
                                serviceInputRefs.current[index]?.blur();
                              }}
                              filterOption={(inputValue, option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase())
                              }
                              placeholder="Select or type a service"
                              style={{ width: "100%" }}
                              size="large"
                            />
                            {formData.services.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveService(index)}
                                className="text-gray-400 hover:text-red-500 shrink-0"
                                aria-label="Remove service"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleAddService}
                        className="mt-2 mb-1 inline-flex items-center gap-1.5 rounded-md bg-[#f4a406] px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#d89005] focus:outline-none focus:ring-2 focus:ring-[#f4a406]/30"
                      >
                        <Plus size={16} />
                        Add more
                      </button>
                    </div>
                  </div>

                  <div className="w-full lg:w-[320px] shrink-0">
                    <p className="text-sm font-medium mb-2 text-gray-600">Preview</p>
                    <div className="lg:sticky lg:top-0">
                      <LoyaltyCardPreview
                        programName={formData.program_name}
                        membershipTitle={formData.program_name}
                        cardTagline={formData.card_tagline}
                        description={formData.description}
                        services={previewServices}
                        membershipType={formData.membership_type}
                        membershipAmount={formData.membership_amount}
                        invitationRequired={formData.invitation_required}
                        logoUrl={logoPreview}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end border-t px-6 py-4">
                  <button
                    type="submit"
                    className="w-full bg-[#f4a406] text-white py-2 rounded-xl hover:bg-[#d89005] transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="w-full bg-white shadow-lg rounded-2xl p-6">
          <div className="grid grid-flow-col grid-rows-3 gap-4">
            <div className="row-start-1 row-end-4">
              <h2 className="text-2xl font-bold mb-4">Membership</h2>
            </div>
            <div className="row-start-1 row-end-4 text-right">
              <button
                type="button"
                onClick={handleOpen}
                className="py-2 px-4 bg-[#f4a406] text-white py-2 rounded-xl hover:bg-[#d89005] transition-colors"
              >
                Add New
              </button>
            </div>
          </div>

          <div className="mb-4">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  loadMemberships(1);
                }
              }}
              placeholder="Search by membership name"
              className="w-full md:w-1/3 px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#f4a406]"
            />
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-gray-700 bg-gray-100">
                <tr>
                  <th className="p-3">Membership Name</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Invitation Only</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Created On</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td className="p-3" colSpan={6}>
                      <ShimmerLoader />
                      <ShimmerLoader />
                      <ShimmerLoader />
                    </td>
                  </tr>
                ) : memberships.length > 0 ? (
                  memberships.map((item) => (
                    <tr key={item.program_id}>
                      <td className="p-3">{item.program_name}</td>
                      <td className="p-3 capitalize">
                        {item.membership_type || "free"}
                        {item.membership_type === "paid" && item.membership_amount
                          ? ` · ${item.membership_amount}`
                          : ""}
                      </td>
                      <td className="p-3">{item.invitation_required ? "Yes" : "No"}</td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(item)}
                          className={`inline-flex items-center gap-1 ${
                            item.is_active ? "text-green-600" : "text-gray-400"
                          }`}
                        >
                          {item.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          {item.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="p-3">{item.createdDate}</td>
                      <td className="p-3 text-center space-x-2">
                        <button onClick={() => handleEdit(item)} className="text-[#f4a406] hover:underline inline-flex items-center gap-1">
                          <Edit size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-3" colSpan={6}>
                      No record found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          </div>
        </div>
      </div>
    </>
  );
}